import {
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Template,
  TemplateNormalized,
  templatesApi,
  TemplateTypes,
  UpdateTemplate,
} from 'apis/templates';
import { FieldTemplate } from 'components/fieldTemplate';
import { HookFormText } from 'components/hookForm';
// eslint-disable-next-line no-restricted-imports
import { helpText } from 'components/shared/templatesModal/utils';
import { validateName } from 'utils/formValidator';

import {
  NotificationType,
  useNotifications,
} from '/assets/hooks/useNotifications';

import { TEMPLATE_NAMES } from '../../../../../../settingsTemplates.view';
import styles from '../styles/templateModal.module.scss';

type SettingsTemplatesFormValues = UpdateTemplate;

type EditTemplateModalProps = {
  templateType: TemplateTypes;
  isModalOpen: boolean;
  onClose: () => void;
  selectedTemplate: TemplateNormalized;
  onEditConfirmed?: (template: Template) => void;
};

export const EditTemplateModal = ({
  templateType,
  isModalOpen,
  onClose,
  selectedTemplate,
  onEditConfirmed,
}: EditTemplateModalProps): JSX.Element => {
  const notify = useNotifications();

  const { handleSubmit, control, formState } =
    useForm<SettingsTemplatesFormValues>({
      defaultValues: selectedTemplate,
      mode: 'onBlur',
    });

  const isOpen = useMemo(() => isModalOpen, [isModalOpen]);

  const [editTemplate] = templatesApi.useUpdateTemplateMutation();

  const onSubmit = useCallback(
    async (formData: SettingsTemplatesFormValues) => {
      const { name, subject, content } = formData;
      const hasSubject = subject && subject.length > 0;

      try {
        const payload = await editTemplate({
          id: formData.id,
          deletes: hasSubject ? [] : ['subject'],
          updates: {
            name,
            content,
            ...(hasSubject && { subject }),
          },
        }).unwrap();

        ampli.settingsTemplatesModalFormSubmitted({
          mode: 'edit',
          templateType,
          templateName: name,
        });

        onEditConfirmed?.(payload.data);

        notify(
          {
            title: 'Template updated',
            text: `Template "${name}"  updated.`,
            icon: 'done',
            time: 2500,
          },
          NotificationType.Success,
        );
      } catch (error) {
        notify(
          {
            title: 'An error occurred',
            text: `An error occurred when trying to update the "${name}" template`,
            icon: 'exclamation',
            time: 2500,
          },
          NotificationType.Error,
        );
      }

      onClose();
    },
    [editTemplate, notify, onClose, onEditConfirmed, templateType],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHead
          title={`Edit ${TEMPLATE_NAMES[templateType].toLowerCase()}`}
          onClose={onClose}
        />
        <ModalBody className={styles.templatesModalBody}>
          <FormGrid>
            <FormField>
              <HookFormText
                name="name"
                label="Template name"
                control={control}
                rules={{
                  required: 'Template name is required',
                  validate: validateName,
                }}
                helpIcon={{
                  title: 'Template name',
                  text: 'Template name should only be in lowercase and not contain any whitespace or special characters',
                }}
                componentProps={{
                  placeholder: 'Template name...',
                }}
              />
            </FormField>

            <FormField>
              <HookFormText
                name="subject"
                control={control}
                label="Subject"
                helpIcon={{
                  title: 'Subject',
                  text: `This field is used to fill out the subject line in emails when composing emails and for auto-replies. The subject line is not used if the template is used in chat or you are replying to an email. You can use these variables: ${helpText}`,
                }}
                componentProps={{
                  placeholder: 'Subject...',
                }}
              />
            </FormField>

            <FormField>
              <div className={styles.templateContentContainer}>
                <Controller
                  name="content"
                  control={control}
                  rules={{
                    required: true,
                  }}
                  render={({ field }) => (
                    <FieldTemplate
                      label="Template content"
                      input={field}
                      placeholder="Content..."
                      height="355px"
                      helpIcon={{
                        title: 'Template Content',
                        text: `The content is written in HTML and supports [Liquid](https://shopify.github.io/liquid/) too. \n\n${helpText}`,
                      }}
                      required
                    />
                  )}
                />
              </div>
            </FormField>
          </FormGrid>
        </ModalBody>
        <ModalFoot
          cancelOnClick={onClose}
          confirmOnClick={handleSubmit(onSubmit)}
          confirmName={`Save ${TEMPLATE_NAMES[
            templateType
          ].toLowerCase()} template`}
          confirmProps={{
            type: 'submit',
            disabled: !formState.isValid || formState.isSubmitting,
          }}
        />
      </form>
    </Modal>
  );
};
